import { SIGNUP_SOURCE_LOCAL_STORAGE_KEY } from '@common/constants'
import { FILE_SIZE_UNITS_IN_BYTES } from '@lib/constants/common'
import { Primitive } from '@lib/types/common'
import { setToLocalStorage } from '@lib/utils/local-storage'
import minimatch from 'minimatch'
import getConfig from 'next/config'
import Router from 'next/router'
import { parseCookies } from 'nookies'
import { captureException } from '@sentry/node'
import { getAPIErrorString } from '@lib/utils/common'

// Keys DRF uses for errors that belong to no particular field. Naming one in
// front of its message tells the reader nothing and looks like a leak -
// "nonFieldErrors: Invalid login credentials provided!" - so these render as
// the sentence alone. A real field keeps its prefix, which is the only thing
// saying which input to go and fix.
const NON_FIELD_ERROR_KEYS = ['nonFieldErrors', 'non_field_errors', 'detail']

const labelError = (key: string, error: unknown) =>
  NON_FIELD_ERROR_KEYS.includes(key) ? `${error}` : `${key}: ${error}`

export const flattenErrorsDictValues = (errorsDict) => {
  if (!errorsDict || typeof errorsDict !== 'object') {
    return []
  }

  const flattenedErrors = []

  // Process the error dictionary
  Object.entries(errorsDict).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      // Handle array values (common Django error format)
      value.forEach((error) => {
        flattenedErrors.push(labelError(key, error))
      })
    } else if (value && typeof value === 'object') {
      // Handle nested objects
      Object.entries(value as Record<string, any>).forEach(
        ([subKey, subValue]) => {
          if (Array.isArray(subValue)) {
            subValue.forEach((error) => {
              flattenedErrors.push(labelError(`${key}.${subKey}`, error))
            })
          } else {
            flattenedErrors.push(labelError(`${key}.${subKey}`, subValue))
          }
        }
      )
    } else if (value) {
      // Handle primitive values
      flattenedErrors.push(labelError(key, value))
    }
  })

  return flattenedErrors
}

export const getCSRBaseUrl = (): string => {
  try {
    const { serverRuntimeConfig } = getConfig()
    if (serverRuntimeConfig?.SSR_API_ENDPOINT)
      return serverRuntimeConfig.SSR_API_ENDPOINT
  } catch (error) {
    // Ignore errors when next/config is unavailable (e.g. tests)
  }

  return process.env.CSR_API_ENDPOINT || ''
}

export const CClient = async (url: string, options: RequestInit = {}) => {
  const cookies = parseCookies()
  const token = cookies.token
  if (!options?.headers?.['Authorization'] && token) {
    options.headers = { ...options.headers, Authorization: `Token ${token}` }
  }

  const endpoint = getCSRBaseUrl()

  let finalUrl = url.startsWith('http') ? url : endpoint + url

  // Add mod_login_user_id as URL parameter if cookie exists
  const modLoginUserId = cookies.mod_login_user_id
  if (modLoginUserId) {
    try {
      const urlObj = new URL(finalUrl)
      urlObj.searchParams.set('mod_login_user_id', modLoginUserId)
      finalUrl = urlObj.toString()
    } catch (error) {
      console.error('Error adding mod_login_user_id to URL:', error)
      showErrorAlert({
        title: 'Error',
        text:
          'An error occurred while processing your request. Please try again later.',
      })
    }
  }

  return fetch(finalUrl, options)
}

export const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.'

/**
 * Read an error response body without assuming it is JSON.
 *
 * A DRF error is JSON, but a dead or overloaded upstream is answered by nginx
 * with an HTML error page. Calling `.json()` on that throws, and a throw in the
 * middle of a submit handler skips whatever was going to re-enable the button.
 */
export const parseErrorResponse = async (
  response: Response
): Promise<Record<string, unknown>> => {
  try {
    return await response.json()
  } catch {
    return { detail: GENERIC_ERROR_MESSAGE }
  }
}

type ErrorAlertProps = {
  title?: string
  text: string
  buttonText?: string
}
export const showErrorAlert = (
  props: ErrorAlertProps,
  onDismiss?: () => void
) => {
  const { title, text, buttonText } = props

  // Callers send "message\n\nError: ...\n\nnext step". The "Error:" part is
  // getAPIErrorString's dump (URL, request body, auth token) - Sentry has it,
  // the reader must not. The whole part goes, since API details can span
  // lines. Parts go in as textContent because they carry server-echoed input.
  const parts = text
    .split(/\n\s*\n/)
    .filter((part) => part.trim() && !part.trimStart().startsWith('Error:'))
  let content: HTMLElement
  if (parts.length > 1) {
    content = document.createElement('div')
    content.style.textAlign = 'center'
    parts.forEach((part) => {
      const p = document.createElement('p')
      p.textContent = part
      content.appendChild(p)
    })
  }

  import('sweetalert').then((swal) => {
    swal
      .default({
        icon: 'error',
        title: title || 'Error!',
        buttons: {
          confirm: {
            text: buttonText ? buttonText : 'Ok',
            value: true,
          },
        },
        content: content ? { element: content } : undefined,
        text: content ? undefined : parts[0] || GENERIC_ERROR_MESSAGE,
        closeOnClickOutside: false,
        closeOnEsc: false,
      })
      .then((val) => {
        if (val && onDismiss) {
          onDismiss()
        }
      })
  })
}

type SuccessAlertProps = {
  title?: string
  text: string
  buttonText?: string
}
export const showSuccessAlert = (
  props: SuccessAlertProps,
  onDismiss?: () => void
) => {
  const { title, text, buttonText } = props

  import('sweetalert').then((swal) => {
    swal
      .default({
        icon: 'success',
        title: title || 'Success!',
        buttons: {
          confirm: {
            text: buttonText ? buttonText : 'Ok',
            value: true,
          },
        },
        text: text,
        closeOnClickOutside: true,
        closeOnEsc: true,
      })
      .then((val) => {
        if (val && onDismiss) {
          onDismiss()
        }
      })
  })
}

export const trackPageVisit = async (page: string, course) => {
  try {
    const response = await CClient('/api/v1/track-page-visit/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page, extraData: { course } }),
    })
    return response.ok
  } catch (error) {
    const { captureException } = await import('@sentry/node')
    captureException(error)
    return false
  }
}

export const staticFile = (path: string) => {
  if (/^https?:\/\//i.test(path)) return path
  if (!path.startsWith('/')) path = '/' + path
  return (process.env.NEXT_PUBLIC_STATIC_URL ?? '') + path
}

type PresignedUploadRequest = {
  studentId: number
  classroomId: number
  fileName: string
  mimeType: string
  fileSize: number
}

type SignedPostPayload = {
  url: string
  fields: Record<string, string>
}

export type ProjectUploadResponse = {
  signedUrl: SignedPostPayload
  assetUrl: string
  key: string
  normalizedFilename: string
  originalFilename: string
  mimeType: string
  assetType: string
  fileSize: number
}

export const requestProjectUploadUrl = async ({
  studentId,
  classroomId,
  fileName,
  mimeType,
  fileSize,
}: PresignedUploadRequest): Promise<ProjectUploadResponse> => {
  const response = await CClient(
    `/api/v1/students/${studentId}/projects/upload/`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        classroomId,
        fileName,
        mimeType,
        fileSize,
      }),
    }
  )

  if (!response.ok) {
    const errorData = await response.json()
    const errors = flattenErrorsDictValues(errorData)
    throw new Error(
      errors.join('\n') || 'Unable to prepare upload destination.'
    )
  }

  const data = await response.json()
  const normalized: ProjectUploadResponse = {
    signedUrl: data.signed_url ?? data.signedUrl,
    assetUrl: data.asset_url ?? data.assetUrl,
    key: data.key,
    normalizedFilename: data.normalized_filename ?? data.normalizedFilename,
    originalFilename: data.original_filename ?? data.originalFilename,
    mimeType: data.mime_type ?? data.mimeType,
    assetType: data.asset_type ?? data.assetType,
    fileSize: data.file_size ?? data.fileSize,
  }

  if (!normalized.signedUrl?.fields || !normalized.signedUrl?.url) {
    throw new Error('Upload initialisation failed. Missing signed URL details.')
  }

  return normalized
}

type UploadWithPresignedArgs = {
  signedUrl: SignedPostPayload
  file: File
  onProgress?: (progress: number) => void
  signal?: AbortSignal
}

export const uploadFileWithPresignedPost = ({
  signedUrl,
  file,
  onProgress,
  signal,
}: UploadWithPresignedArgs): Promise<void> =>
  new Promise((resolve, reject) => {
    const formData = new FormData()
    Object.entries(signedUrl.fields || {}).forEach(([key, value]) =>
      formData.append(key, value)
    )
    formData.append('file', file)

    const xhr = new XMLHttpRequest()

    if (signal) {
      signal.addEventListener('abort', () => {
        xhr.abort()
        reject(new DOMException('Upload aborted', 'AbortError'))
      })
    }

    xhr.upload.addEventListener('progress', (event) => {
      if (!onProgress || !event.lengthComputable) return
      const percentage = Math.round((event.loaded / event.total) * 100)
      onProgress(percentage)
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error('Upload failed. Please try again.'))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed. Please try again.'))
    })

    xhr.open('POST', signedUrl.url)
    xhr.send(formData)
  })

export const getLinkWithNextParam = (link: string, next: string) => {
  if (next) return `${link}?next=${next}`
  return link
}

export const getPriceWithCommas = (x: number) => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const getPaymentPageLinkWithParam = (
  paymentPageLink: string,
  campaign: string,
  name: string,
  email: string,
  phone: string
) =>
  `${paymentPageLink}?campaign=${campaign}&child_name=${name}&email=${email}&phone=${phone}`

export const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

export const copyToClipboard = (contentToCopy: string): Promise<void> =>
  navigator.clipboard.writeText(contentToCopy)

export const handleByteConversion = (
  fileSizeInBytes: number,
  decimalPlaces: number
) => {
  if (fileSizeInBytes >= FILE_SIZE_UNITS_IN_BYTES.GB)
    return `${(fileSizeInBytes / FILE_SIZE_UNITS_IN_BYTES.GB).toFixed(
      decimalPlaces
    )} GB`

  if (fileSizeInBytes >= FILE_SIZE_UNITS_IN_BYTES.MB)
    return `${(fileSizeInBytes / FILE_SIZE_UNITS_IN_BYTES.MB).toFixed(
      decimalPlaces
    )} MB`

  if (fileSizeInBytes >= FILE_SIZE_UNITS_IN_BYTES.KB)
    return `${(fileSizeInBytes / FILE_SIZE_UNITS_IN_BYTES.KB).toFixed(
      decimalPlaces
    )} KB`

  return `${fileSizeInBytes.toFixed(decimalPlaces)} B`
}

export const getParsedTime = (timeInSeconds: number) => {
  const days = Math.floor(timeInSeconds / (3600 * 24))
  const hours = Math.floor((timeInSeconds % (3600 * 24)) / 3600)
  const minutes = Math.floor((timeInSeconds % 3600) / 60)
  const seconds = Math.floor(timeInSeconds % 60)

  const dayString = days > 0 ? days + ' d' : ''
  let hourString = hours > 0 ? hours + (hours == 1 ? ' hr' : ' hrs') : ''
  let minuteString =
    minutes > 0 ? minutes + (minutes == 1 ? ' min' : ' mins') : ''
  let secondString =
    seconds > 0 ? seconds + (seconds == 1 ? ' sec' : ' secs') : ''

  const addLeadingComma = (timeString: string) => ', ' + timeString

  if (hourString && dayString) hourString = addLeadingComma(hourString)
  if (minuteString && (dayString || hourString))
    minuteString = addLeadingComma(minuteString)
  if (secondString && (dayString || hourString || minuteString))
    secondString = addLeadingComma(secondString)

  return dayString + hourString + minuteString + secondString
}

/**
 * Strip the query string and hash off a path so only the page itself is kept.
 *
 * Callers pass `router.asPath`, which carries the UTM / gclid params of the
 * landing URL. Those belong to `leadSource`; storing them in `signupSource`
 * makes every ad-driven signup a distinct source and overflows the column.
 */
export const getSignupSourcePath = (path: string) =>
  path ? path.split('?')[0].split('#')[0] : path

export const handleRedirectionWithSignupSource = (
  signupSource: string,
  redirectionURL: string
) => {
  const signupSourcePath = getSignupSourcePath(signupSource)
  setToLocalStorage(SIGNUP_SOURCE_LOCAL_STORAGE_KEY, signupSourcePath, () => {
    import('@sentry/node').then((s) =>
      s.captureMessage(`Unable to store signup source ${signupSourcePath}`)
    )
  })
  Router.push(
    getUrlWithQueryString(redirectionURL, Router.asPath.split('?')[1])
  )
}

export const removePhoneParam = (url: string) => {
  const baseUrl = url.split('?')[0]
  const paramStr = url.split('?')?.[1]
  if (!paramStr) return baseUrl
  const params = new URLSearchParams(`?${paramStr}`)
  params.has('phone') && params.delete('phone')
  return baseUrl + '?' + params.toString()
}

export const getUrlWithQueryString = (url: string, queryString: string) => {
  // Handling the case where url passed may have query parameters
  const [urlWithoutQueryString, urlQueryString] = url.split('?')
  const finalQueryString = `${queryString || ''}${
    queryString && urlQueryString ? '&' : ''
  }${urlQueryString || ''}`

  const finalURL =
    urlWithoutQueryString + (finalQueryString ? `?${finalQueryString}` : '')
  return finalURL
}

export const queryStringToObject = (queryString?: string) => {
  if (!queryString) return null
  const params = new URLSearchParams(queryString)
  return Array.from(params.entries()).reduce((prev, [key, value]) => {
    return {
      ...prev,
      [key]: value,
    }
  }, {})
}

/**
 *
 * @param object The base object eg {xyz:123}
 * @param paths An array of paths to omit. [xyz]
 */
export const omit = (object: Record<string, Primitive>, paths: string[]) => {
  if (!object || !paths.length) return null

  const newObject = { ...object }
  for (const path of paths) {
    delete newObject[path]
  }
  return newObject
}

/**
 * Check if a route path is available in the provided route array.
 * @param route | string route
 * @param routeEndPoints | string route endpoints to test against
 */
export const hasRoute = (route: string, routeEndPoints: string[]) => {
  return routeEndPoints.find((element) => minimatch(route, element))
}

/**
 * Check if the provided numbers are Indian numbers
 * @param {string} numbers | array of numbers
 */
export const areIndianNumbers = (numbers: string[]) => {
  return numbers?.every((number) => number?.startsWith('+91'))
}

/**
 * This is the javascript alternative of unix sleep function
 * use this only on development not in prod unless required
 * @param {number} ms | time to wait in miliseconds
 */
export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export const getIsBirthday = (dob, timezone) => {
  if (!dob || !timezone) return false

  const currentDate = new Date(
    new Date().toLocaleDateString('en-US', {
      timeZone: timezone,
    })
  )
  const birthDate = new Date(dob)

  const currentMonth = currentDate.getMonth()
  const currentDay = currentDate.getDate()
  const birthMonth = birthDate.getMonth()
  const birthday = birthDate.getDate()

  return currentMonth === birthMonth && currentDay === birthday
}

export const updateUserSchool = async (
  userId: number,
  schoolId,
  schoolName
) => {
  // Nothing to link, and nothing to create. School is optional on the
  // post-booking modal, so a blank name reaches here on every save where the
  // parent skipped it - without this it creates an empty-titled school and
  // links the user to it.
  if (!schoolId && !schoolName?.trim()) return

  try {
    // if school id is null, create a new school then update the user school
    const updatedSchoolId = !schoolId
      ? await addNewSchoolToDatabase(schoolName)
      : schoolId
    const requestUrl = '/api/v1/user-schools/'
    const requestBody = JSON.stringify({ schoolId: updatedSchoolId, userId })
    const response = await CClient(requestUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: requestBody,
    })
    if (!response.ok)
      throw new Error(
        await getAPIErrorString(response, requestUrl, requestBody)
      )
  } catch (error) {
    captureException(error)
  }
}

export const addNewSchoolToDatabase = async (schoolName: string) => {
  const requestUrl = '/api/v1/schools/'
  const requestBody = JSON.stringify({ title: schoolName })
  const response = await CClient(requestUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: requestBody,
  })
  if (!response.ok)
    throw new Error(await getAPIErrorString(response, requestUrl, requestBody))

  const { id } = await response.json()
  return id
}

export function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  })
}

// Load the external JS script dynamically
export function loadScript(src: string) {
  return new Promise((resolve) => {
    const script = document.createElement('script')
    script.src = src
    script.onload = () => resolve(true)
    script.onerror = () => resolve(false)
    document.body.appendChild(script)
  })
}

/**
 * Fixes URLs with double slashes (except for protocol part)
 * Example: https://domain.com//media/file.mp4 -> https://domain.com/media/file.mp4
 */
export const fixVideoUrl = (url: string): string => {
  if (!url) return url

  // Replace multiple consecutive slashes with a single slash,
  // but preserve the protocol part (https://)
  return url.replace(/([^:]\/)\/+/g, '$1')
}

export function currencyCodeToSymbol(amount?: number, currencyCode?: string) {
  try {
    const currencySymbol = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode ?? 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)

    const formattedAmount = currencySymbol.replace(/(\D+)(\d+)/, '$1$2')

    if (formattedAmount.endsWith('.00')) {
      return formattedAmount.slice(0, -3)
    }
    return formattedAmount
  } catch (error) {
    return amount
  }
}
